import type { Platform } from './platform';
import { PlatformEnum } from '../utils/types';
import { EnvironmentDetector } from './detector';
import { WebAdapter, LiteappAdapter } from '../adapters';

/**
 * 平台工厂
 * 负责创建和管理平台适配器实例
 */
export class PlatformFactory {
  private static instance: PlatformFactory;
  private platformInstance: Platform | null = null;
  private currentPlatform: PlatformEnum | null = null;

  private constructor() {}

  /**
   * 获取单例实例
   */
  static getInstance(): PlatformFactory {
    if (!this.instance) {
      this.instance = new PlatformFactory();
    }
    return this.instance;
  }

  /**
   * 创建平台适配器
   */
  create(): Platform {
    if (!this.platformInstance) {
      const platformType = this.getCurrentPlatform();
      this.platformInstance = this.createAdapter(platformType);
      this.currentPlatform = platformType;
    }
    return this.platformInstance;
  }

  /**
   * 获取当前平台类型
   */
  getCurrentPlatform(): PlatformEnum {
    if (!this.currentPlatform) {
      this.currentPlatform = EnvironmentDetector.detectPlatform();
    }
    return this.currentPlatform;
  }

  /**
   * 重置工厂实例
   * 主要用于测试和重新检测环境
   */
  reset(): void {
    this.platformInstance = null;
    this.currentPlatform = null;
  }

  /**
   * 强制使用指定平台
   * 主要用于测试和调试
   */
  forcePlatform(platformType: PlatformEnum): void {
    this.currentPlatform = platformType;
    this.platformInstance = this.createAdapter(platformType);
  }

  /**
   * 创建适配器实例
   */
  private createAdapter(platformType: PlatformEnum): Platform {
    switch (platformType) {
      case PlatformEnum.LiteApp:
        return new LiteappAdapter();
      case PlatformEnum.Miniprogram:
        // TODO: 实现MiniProgramAdapter
        return new WebAdapter(); // 现在用不到，先临时使用Web适配器
      case PlatformEnum.Web:
      default:
        return new WebAdapter();
    }
  }

  /**
   * 获取平台信息
   */
  getPlatformInfo(): {
    platformType: PlatformEnum;
    version?: string;
    capabilities: Record<string, boolean>;
    } {
    // 获取环境信息
    const env = EnvironmentDetector.getEnvironmentInfo();
    // 获取平台能力
    const capabilities = EnvironmentDetector.detectCapabilities(env.platformType);
    // 获取平台版本
    const version = EnvironmentDetector.getPlatformVersion(env.platformType);

    const result: {
      platformType: PlatformEnum;
      version?: string;
      capabilities: Record<string, boolean>;
    } = {
      platformType: env.platformType,
      capabilities: Object.fromEntries(
        Object.entries(capabilities).map(([key, value]) => [key, !!value]),
      ),
    };

    if (version) {
      result.version = version;
    }

    return result;
  }

  /**
   * 检查是否支持指定平台
   */
  isSupported(platformType: PlatformEnum): boolean {
    const supportedPlatforms: PlatformEnum [] = [PlatformEnum.Web, PlatformEnum.LiteApp];
    return supportedPlatforms.includes(platformType);
  }

  /**
   * 获取所有支持的平台
   */
  getSupportedPlatforms(): PlatformEnum [] {
    // TODO: 实现MiniprogramAdapter和KboneAdapter后，添加到列表中
    return [PlatformEnum.Web, PlatformEnum.LiteApp];
  }

  /**
   * 启用调试模式
   */
  enableDebug(enabled = true): void {
    if (this.platformInstance) {
      // 如果已经有实例，需要重新创建
      this.reset();
    }
    // 调试信息会在适配器创建时输出
    console.log(`[PlatformFactory] Debug mode ${enabled ? 'enabled' : 'disabled'}`);
  }
}
